<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-card class="mx-auto mt-5" outlined tile>
          <v-toolbar flat>
            <v-spacer />
            <v-btn text color="primary" @click="add">
              <v-icon>add</v-icon>
              Add
            </v-btn>
          </v-toolbar>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">
                    Name
                  </th>
                  <th class="text-left">
                    Value
                  </th>
                  <th class="text-left">
                    Agreement URL
                  </th>
                  <th class="text-left">
                    F2F Training Required
                  </th>
                  <th class="text-left">
                    Site Visit Required
                  </th>
                  <th class="text-left">
                    End User Portal
                  </th>
                  <th class="text-left">
                    Producer Portal
                  </th>
                  <th class="text-left">
                    Self Registration
                  </th>
                  <th class="text-left">
                    Auto Finalise
                  </th>
                  <th class="text-end">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="memberType in memberTypes"
                  :key="memberType.value"
                >
                  <td>{{ memberType.name }}</td>
                  <td>{{ memberType.value }}</td>
                  <td><a target="_blank" :href="memberType.agreementUrl || LICENSE_AGREEMENT_GENERIC">{{ memberType.agreementUrl || LICENSE_AGREEMENT_GENERIC }}</a></td>
                  <td>{{ memberType.trainingRequired | yesNo }}</td>
                  <td>{{ memberType.siteVisitRequired | yesNo }}</td>
                  <td>{{ memberType.enableEndUserPortal | yesNo }}</td>
                  <td>{{ memberType.enableProducerPortal | yesNo }}</td>
                  <td>{{ memberType.enableSelfRegistration | yesNo }}</td>
                  <td>{{ memberType.enableAutoFinalise | yesNo }}</td>
                  <td class="text-end">
                    <v-btn
                      small
                      icon
                      @click="edit(memberType)"
                      class="align-end"
                    >
                      <v-icon>edit</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog
      v-model="dialog"
      width="500"
    >
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          <template v-if="form._id">
            Editing {{ form.name }}
          </template>
          <template v-else>
            New member type
          </template>
        </v-card-title>

        <v-card-text>
          <v-form>
            <v-subheader>System Details</v-subheader>
            <v-text-field
              v-model="form.name"
              label="Member type name"
              hint="This cannot be changed after the member type is saved."
              persistent-hint
            />
            <v-text-field
              :value="form.value"
              label="System value"
              disabled
              hint="System generated internal value. This cannot be changed."
              persistent-hint
            />
            <v-select
              label="Relevant details"
              hint="Select the fields that are relevant to this member type. These fields will be fillable during registrations for this member type."
              persistent-hint
              v-model="form.relevantDetails"
              :items="activityDetailAll"
              item-text="path"
              item-value="path"
              multiple
              chips
            />

            <v-subheader class="mt-5">Registration Portal</v-subheader>
            <v-text-field
              v-model="form.agreementUrl"
              label="Agreement URL"
              :placeholder="LICENSE_AGREEMENT_GENERIC"
              hint="URL to public PDF of the license agreement for this member type. This PDF is presented to registrants to accept digitally."
            />
            <v-checkbox
              v-model="form.trainingRequired"
              label="Face-to-face training required"
              hint="When enabled, indicates to registrants that face-to-face training is required."
            />
            <v-checkbox
              v-model="form.siteVisitRequired"
              label="Site visit required"
              hint="When enabled, indicates to registrants that a site visit is required."
            />
            <v-checkbox
              v-model="form.enableSelfRegistration"
              label="Enable self-registration"
              hint="When enabled, allows registrants to select this member type during registration."
              persistent-hint
            />
            <v-checkbox
              v-model="form.enableEndUserPortal"
              label="Enable end user portal"
              hint="When enabled, requires the applicant to complete the end-user portal assessment before submitting a registration."
              persistent-hint
            />
            <v-checkbox
              v-model="form.enableProducerPortal"
              label="Enable producer portal"
              hint="When enabled, requires the applicant to complete the producer portal assessment before submitting a registration."
              persistent-hint
            />
            <v-checkbox
              v-model="form.enableAutoFinalise"
              label="Enable auto-finalise"
              hint="When enabled, automatically finalises submitted registrations, allowing immediate MSA accreditation if intervention not needed. NOTE: If enabled, training and site visit MUST be disabled."
              persistent-hint
            />
            <v-textarea
              rows="2"
              v-model="form.description"
              label="Description"
              hint="When supplied, this description will appear during the registration process to describe the member type."
            />
            <v-textarea
              rows="4"
              v-model="form.prerequisitesDescription"
              label="Prerequisites"
              hint="When supplied, this description will appear during the registration process to describe the prerequisites before completing a registration."
            />

            <v-subheader class="mt-5">Sub-Types</v-subheader>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>
                      Name
                    </th>
                    <th>
                      System Name
                    </th>
                    <th>
                      Active
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="subType in form.subTypes" :key="subType._id">
                    <td>
                      {{ subType.name }}
                    </td>
                    <td>
                      {{ subType.value }}
                    </td>
                    <td>
                      <v-checkbox v-model="subType.isActive" />
                    </td>
                  </tr>

                  <tr>
                    <td colspan="2">
                      <v-text-field
                        v-model="subTypeForm.name"
                      />
                    </td>
                    <td class="text-end">
                      <v-btn
                        color="primary"
                        outlined
                        @click="addSubType"
                      >
                        <v-icon>add</v-icon>
                        Add
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="save"
          >
            Save
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="dialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import gql from 'graphql-tag'
import { constantCase } from 'change-case'

import { LICENSE_AGREEMENT_GENERIC } from '../utilities/env.js'

export default {
  name: 'AdminMemberTypeListView',

  data: () => ({
    LICENSE_AGREEMENT_GENERIC,
    memberTypes: [],
    activityDetailAll: [],
    dialog: false,
    form: {
      subTypes: []
    },
    subTypeForm: {}
  }),

  apollo: {
    memberTypes: {
      query: gql`query {
        memberTypes: memberTypeMany(limit: 0, sort: VALUE_ASC) {
          _id
          name
          value
          agreementUrl
          trainingRequired
          siteVisitRequired
          enableSelfRegistration
          enableEndUserPortal
          enableProducerPortal
          enableAutoFinalise
          description
          prerequisitesDescription
          relevantDetails
          subTypes {
            _id
            name
            value
            isActive
          }
        }
      }`,
      error (e) { this.$emit('error', e) }
    },
    activityDetailAll: {
      query: gql`query {
        activityDetailAll {
          path
        }
      }`,
      error (e) { this.$emit('error', e) }
    }
  },

  methods: {
    onError (e) {
      this.$emit('error', e)
    },

    async save () {
      try {
        let mutation
        if (this.form._id) {
          mutation = {
            mutation: gql`mutation ($id: MongoID!, $record: UpdateByIdMemberTypeInput!) {
              memberTypeUpdateById(_id: $id, record: $record) {
                record {
                  _id
                  trainingRequired
                  siteVisitRequired
                  agreementUrl
                  enableSelfRegistration
                  enableEndUserPortal
                  enableProducerPortal
                  description
                  prerequisitesDescription
                  relevantDetails
                  subTypes {
                    _id
                    name
                    value
                    isActive
                  }
                }
              }
            }`,
            variables: {
              id: this.form._id,
              record: {
                trainingRequired: this.form.trainingRequired,
                siteVisitRequired: this.form.siteVisitRequired,
                agreementUrl: this.form.agreementUrl,
                enableSelfRegistration: this.form.enableSelfRegistration,
                enableEndUserPortal: this.form.enableEndUserPortal,
                enableProducerPortal: this.form.enableProducerPortal,
                enableAutoFinalise: this.form.enableAutoFinalise,
                description: this.form.description,
                prerequisitesDescription: this.form.prerequisitesDescription,
                relevantDetails: this.form.relevantDetails,
                subTypes: this.form.subTypes
              }
            }
          }
        } else {
          mutation = {
            mutation: gql`mutation ($record: CreateOneMemberTypeInput!) {
              memberTypeCreateOne(record: $record) { record { _id } }
            }`,
            variables: {
              record: this.form
            }
          }
        }
        await this.$apollo.mutate(mutation)
      } catch (e) {
        this.onError(e)
        return
      }
      this.dialog = false
      this.$apollo.queries.memberTypes.refresh()
    },

    add () {
      this.form = {
        name: '',
        value: '',
        agreementUrl: '',
        trainingRequired: false,
        siteVisitRequired: false,
        enableSelfRegistration: false,
        enableEndUserPortal: false,
        enableProducerPortal: false,
        enableAutoFinalise: false,
        description: null,
        prerequisitesDescription: null,
        relevantDetails: [],
        subTypes: []
      }
      this.dialog = true
    },

    edit (memberType) {
      this.form = {
        _id: memberType._id,
        name: memberType.name,
        value: memberType.value,
        agreementUrl: memberType.agreementUrl,
        trainingRequired: memberType.trainingRequired,
        siteVisitRequired: memberType.siteVisitRequired,
        enableSelfRegistration: memberType.enableSelfRegistration,
        enableEndUserPortal: memberType.enableEndUserPortal,
        enableProducerPortal: memberType.enableProducerPortal,
        enableAutoFinalise: memberType.enableAutoFinalise,
        description: memberType.description,
        prerequisitesDescription: memberType.prerequisitesDescription,
        relevantDetails: memberType.relevantDetails ?? [],
        subTypes: memberType.subTypes ?? []
      }
      this.dialog = true
    },

    addSubType () {
      const subType = {
        name: this.subTypeForm.name,
        value: constantCase(this.subTypeForm.name),
        isActive: true
      }
      this.form.subTypes.push(subType)
      this.subTypeForm = {}
    }
  },

  watch: {
    'form.name' (name) {
      if (typeof name !== 'string') return
      this.form.value = constantCase(name)
    }
  }
}
</script>
